import { CLIENT_DOCUMENT_TYPES, formatClientDocument, normalizeClientDocument } from "./validationHelper";

export const TAX_ID_TYPES = CLIENT_DOCUMENT_TYPES;

export const TAX_ID_LENGTHS = Object.freeze({ CPF: 11, CNPJ: 14 });

// Per-file limits from the API contract v2 (files go through the documents upload, then are checked by Bedrock).
export const TAX_ID_FILE_LIMITS = Object.freeze({
  maxFiles: 2,
  maxImageDimension: 2000,
  jpegQuality: 0.85,
  maxImageBytes: 3750000,
  maxPdfBytes: 4500000,
  // Raw photos are downscaled before upload; this only guards the browser against huge inputs.
  maxRawImageBytes: 25 * 1024 * 1024,
});

// Category for files uploaded through POST /files for a correction request.
export const TAX_ID_DOCUMENT_CATEGORY = "ID_DOCUMENT";

// Second pass used only when an image still exceeds maxImageBytes after the first downscale.
export const TAX_ID_COMPACT_IMAGE = Object.freeze({ maxDimension: 1600, quality: 0.75 });

export const TAX_ID_ACCEPTED_TYPES = Object.freeze(["image/jpeg", "image/png", "application/pdf"]);
export const TAX_ID_FILE_ACCEPT = "image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf";
export const TAX_ID_CAMERA_ACCEPT = "image/*";

// Applies to the correction POST only (not the uploads). API Gateway stops at 29s; the request must never be
// retried automatically (it would duplicate the task).
export const TAX_ID_REQUEST_TIMEOUT_MS = 45000;

const allSameDigits = (digits) => /^(\d)\1*$/.test(digits);

export const isValidCpfChecksum = (value) => {
  const digits = normalizeClientDocument(value);
  if (digits.length !== 11 || allSameDigits(digits)) return false;
  const check = (length) => {
    let sum = 0;
    for (let i = 0; i < length; i += 1) sum += Number(digits[i]) * (length + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return check(9) === Number(digits[9]) && check(10) === Number(digits[10]);
};

export const isValidCnpjChecksum = (value) => {
  const digits = normalizeClientDocument(value);
  if (digits.length !== 14 || allSameDigits(digits)) return false;
  const check = (length) => {
    const weights = length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = weights.reduce((acc, weight, i) => acc + Number(digits[i]) * weight, 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return check(12) === Number(digits[12]) && check(13) === Number(digits[13]);
};

export const isValidTaxId = (value, type) =>
  (type === TAX_ID_TYPES.CNPJ ? isValidCnpjChecksum(value) : isValidCpfChecksum(value));

export const maskTaxIdInput = (value, type) => formatClientDocument(value, type);

const isMasked = (value) => typeof value === "string" && value.includes("*");

// Shows only the last 4 digits of the current number, e.g. "***.***.*89-09" / "**.***.***/**01-95".
// Full formatted number for the modal (user-directed: no masking). Values the API already masked are shown as received.
export const formatCurrentTaxId = (value, type) => {
  if (value == null || String(value).trim() === "") return "-";
  if (isMasked(value)) return String(value);
  const digits = normalizeClientDocument(value);
  if (digits.length !== TAX_ID_LENGTHS[type]) return String(value);
  return type === TAX_ID_TYPES.CNPJ
    ? `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
    : `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

// Party details carry either `cpf` or `cnpj`; the request type follows what the party already has.
export const resolveTaxIdType = (details = {}) =>
  (!details?.cpf && details?.cnpj ? TAX_ID_TYPES.CNPJ : TAX_ID_TYPES.CPF);

export const currentTaxIdValue = (details = {}, type = resolveTaxIdType(details)) =>
  (type === TAX_ID_TYPES.CNPJ ? details?.cnpj : details?.cpf) || "";

// Returns an i18n key under taxIdChange.errors, or null when the value can be sent.
export const validateNewTaxId = (value, type, currentValue) => {
  const digits = normalizeClientDocument(value);
  if (!digits) return "required";
  if (digits.length !== TAX_ID_LENGTHS[type]) return type === TAX_ID_TYPES.CNPJ ? "cnpjLength" : "cpfLength";
  if (!isValidTaxId(digits, type)) return type === TAX_ID_TYPES.CNPJ ? "cnpjChecksum" : "cpfChecksum";
  if (!isMasked(currentValue) && normalizeClientDocument(currentValue) === digits) return "sameAsCurrent";
  return null;
};

// The contract adds these fields to GET /clients/parties/{id}; accept them at the top level or under `details`.
const partyField = (party, field) => (party?.[field] !== undefined ? party[field] : party?.details?.[field]);

const isDevForced = (env) => env.NODE_ENV === "development" && env.REACT_APP_FORCE_TAX_ID_CHANGE === "true";

// `taxIdChange` comes from /features; REACT_APP_FORCE_TAX_ID_CHANGE only applies in development.
export const isTaxIdChangeEnabled = (features, env = process.env) =>
  features?.taxIdChange === true || isDevForced(env);

export const getPendingTaxIdChange = (party) => {
  const pending = partyField(party, "pendingTaxIdChange");
  return pending && typeof pending === "object" ? pending : null;
};

// The backend sends `canRequestTaxIdChange` (flag on, caller owns the party, nothing pending).
// The dev force only applies while the backend does not send the field yet.
export const canRequestPartyTaxIdChange = (features, party, { forbidden = false } = {}, env = process.env) => {
  if (!party || !isTaxIdChangeEnabled(features, env) || forbidden || getPendingTaxIdChange(party)) return false;
  const value = partyField(party, "canRequestTaxIdChange");
  if (value === true) return true;
  return value === undefined && isDevForced(env);
};

export const getTaxIdChangeMatters = (party, fallbackMatter) => {
  const matters = partyField(party, "taxIdChangeMatters");
  if (Array.isArray(matters)) return matters.filter((matter) => matter && matter.id);
  return fallbackMatter?.id ? [fallbackMatter] : [];
};

// "2026-10-14" -> "14/10"; ISO timestamps use the browser's local date.
export const formatDayMonth = (value) => {
  if (!value) return "";
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}`;
};

export const scaleDimensions = (width, height, maxDimension = TAX_ID_FILE_LIMITS.maxImageDimension) => {
  const longest = Math.max(width, height);
  if (!longest || longest <= maxDimension) return { width, height };
  const ratio = maxDimension / longest;
  return { width: Math.max(1, Math.round(width * ratio)), height: Math.max(1, Math.round(height * ratio)) };
};

// Base64 grows the body by 4/3; the backend limits the decoded bytes.
const fileExtension = (name = "") => {
  const match = /\.([a-z0-9]+)$/i.exec(name);
  return match ? match[1].toLowerCase() : "";
};

export const detectContentType = (file) => {
  const type = String(file?.type || "").toLowerCase();
  if (type === "image/jpg") return "image/jpeg";
  if (type) return type;
  const ext = fileExtension(file?.name);
  if (ext === "pdf") return "application/pdf";
  if (ext === "png") return "image/png";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  return "";
};

export const isImageContentType = (type) => type === "image/jpeg" || type === "image/png" || /^image\/(heic|heif|webp)$/.test(type);

// Validates a picked file before any processing. Returns an i18n key under taxIdChange.errors, or null.
export const validatePickedFile = (file) => {
  const type = detectContentType(file);
  if (type === "application/pdf") return file.size > TAX_ID_FILE_LIMITS.maxPdfBytes ? "pdfTooLarge" : null;
  if (!isImageContentType(type)) return "fileType";
  if (file.size > TAX_ID_FILE_LIMITS.maxRawImageBytes) return "imageTooLarge";
  return null;
};

export const isPreparedFileTooLarge = (file) => file.size > (file.contentType === "application/pdf"
  ? TAX_ID_FILE_LIMITS.maxPdfBytes
  : TAX_ID_FILE_LIMITS.maxImageBytes);

// Returns an i18n key when the prepared files break the per-file limits.
export const validatePreparedFiles = (prepared) => {
  if (!prepared.length) return "fileRequired";
  if (prepared.length > TAX_ID_FILE_LIMITS.maxFiles) return "tooManyFiles";
  return prepared.some(isPreparedFileTooLarge) ? "payloadTooLarge" : null;
};

const loadImage = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (error) {
      // Fall back to <img> (e.g. Safari versions without options support).
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("IMAGE_DECODE_FAILED"));
    };
    img.src = url;
  });
};

const canvasToBlob = (canvas, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("IMAGE_ENCODE_FAILED"))), "image/jpeg", quality);
});

// Re-encodes any image as JPEG with its longest side capped at 2000px (white background for PNG transparency).
export const downscaleImage = async (
  file,
  {
    maxDimension = TAX_ID_FILE_LIMITS.maxImageDimension,
    quality = TAX_ID_FILE_LIMITS.jpegQuality,
    load = loadImage,
    createCanvas = () => document.createElement("canvas"),
    toBlob = canvasToBlob,
  } = {},
) => {
  const image = await load(file);
  const sourceWidth = image.width || image.naturalWidth;
  const sourceHeight = image.height || image.naturalHeight;
  const { width, height } = scaleDimensions(sourceWidth, sourceHeight, maxDimension);
  const canvas = createCanvas();
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  if (typeof image.close === "function") image.close();
  return toBlob(canvas, quality);
};

const jpegName = (name, fallback) => {
  const base = String(name || "").replace(/\.[^.]+$/, "").replace(/[^\w.-]+/g, "-").slice(0, 80);
  return `${base || fallback}.jpg`;
};

// Turns a picked file into the shape sent to the API (without base64 yet), downscaling images.
export const prepareTaxIdFile = async (file, fallbackName = "documento", options = {}) => {
  const type = detectContentType(file);
  if (type === "application/pdf") {
    return { fileName: file.name || `${fallbackName}.pdf`, contentType: type, blob: file, size: file.size };
  }
  const blob = await (options.downscale || downscaleImage)(file, options);
  return {
    fileName: jpegName(file.name, fallbackName),
    contentType: "image/jpeg",
    blob,
    size: blob.size,
    source: file,
    compact: Boolean(options.compact),
  };
};

export const canCompactPreparedFile = (file) => Boolean(file?.source && !file.compact && file.contentType === "image/jpeg");

export const canCompactOversizedFile = (file) => canCompactPreparedFile(file) && isPreparedFileTooLarge(file);

// True when some image is over its limit but can still be re-encoded smaller.
export const needsCompaction = (files) => files.some(canCompactOversizedFile);

export const compactTaxIdFile = (file, fallbackName, prepare = prepareTaxIdFile) =>
  prepare(file.source, fallbackName, { ...TAX_ID_COMPACT_IMAGE, compact: true });

// The documents upload reads `name`/`type`; downscaled images are bare Blobs.
export const toUploadFile = (prepared) => {
  if (typeof File === "function") {
    return new File([prepared.blob], prepared.fileName, { type: prepared.contentType });
  }
  return Object.assign(prepared.blob, { name: prepared.fileName });
};

// Body of POST .../tax-id-change-request (contract v2: files are referenced by their uploaded ids).
export const buildTaxIdChangeRequest = ({ type, newValue, matterId, fileIds }) => ({
  type,
  newValue: normalizeClientDocument(newValue),
  matterId,
  fileIds,
});

// Maps API error codes (contract v2) to i18n keys under taxIdChange.errors.
const ERROR_KEYS = Object.freeze({
  VALIDATION_ERROR: "validation",
  FILE_NOT_FOUND: "fileMissing",
  FILE_NOT_ON_MATTER: "fileMissing",
  FILE_TOO_LARGE: "payloadTooLarge",
  UPLOAD_FAILED: "uploadFailed",
  FORBIDDEN: "forbidden",
  NOT_FOUND: "notFound",
  REQUEST_PENDING: "requestPending",
  PAYLOAD_TOO_LARGE: "payloadTooLarge",
  RATE_LIMITED: "rateLimited",
  UPSTREAM_ERROR: "upstream",
  TIMEOUT: "timeout",
  NETWORK: "network",
});

const STATUS_CODES = Object.freeze({
  400: "VALIDATION_ERROR",
  403: "FORBIDDEN",
  404: "NOT_FOUND",
  409: "REQUEST_PENDING",
  413: "PAYLOAD_TOO_LARGE",
  429: "RATE_LIMITED",
  502: "UPSTREAM_ERROR",
  504: "TIMEOUT",
});

export const resolveTaxIdErrorCode = (error) => {
  if (error?.code && ERROR_KEYS[error.code]) return error.code;
  if (error?.status && STATUS_CODES[error.status]) return STATUS_CODES[error.status];
  return null;
};

export const taxIdErrorKey = (error) => ERROR_KEYS[resolveTaxIdErrorCode(error)] || "generic";

// Field-level codes the backend may send in fieldErrors (values), mapped to i18n keys.
const FIELD_ERROR_KEYS = Object.freeze({
  SAME_AS_CURRENT: "sameAsCurrent",
  INVALID_CHECKSUM: "checksum",
  MATTER_REQUIRED: "matterRequired",
});

// Which step the user must go back to for a given server field error.
export const TAX_ID_FIELD_STEPS = Object.freeze({ newValue: "value", type: "value", files: "document", matterId: "document" });

export const fieldErrorKey = (value) => {
  const code = String(value || "").trim().toUpperCase();
  return FIELD_ERROR_KEYS[code] || null;
};
